import type { UseQueryResult } from '@tanstack/react-query'
import { type Dispatch, type FC, type SetStateAction, useMemo } from 'react'
import { ErrorBox } from '@/components/commons/error-box'
import { AlertCircleIcon } from '@/components/ui/icons'
import { ListHeader } from '@/components/ui/list-header'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Scrollbar } from '@/components/ui/scrollbar'
import { SpaceCard } from '@/features/photoset/components/space-card'
import { SpaceDetailModal } from '@/features/photoset/components/space-detail-modal'
import { usePhotosetStore } from '@/features/photoset/store/store'
import type { Image, PhotosetSpace } from '@/features/photoset/types'
import {
	spaceNameToLabel,
	spaceNameToShortcut
} from '@/features/photoset/utils'
import { objectValues } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'

const SpacesSidebarContent = ({
	queryStatuses,
	photosetSpaces,
	images,
	unselectAll,
	setCurrentSpaceIndex
}: {
	queryStatuses: UseQueryResult['status'][]
	photosetSpaces: PhotosetSpace[]
	images: Map<number, Image>
	unselectAll: () => void
	setCurrentSpaceIndex: Dispatch<SetStateAction<number | null>>
}) => {
	const { t } = useTranslation()
	if (queryStatuses.includes('pending')) {
		return Array.from({ length: 6 }, (_, i) => `skeleton-${i}`).map(
			(key) => <SpaceCard asSkeleton key={key} />
		)
	}
	if (queryStatuses.includes('error')) {
		return (
			<ErrorBox icon={<AlertCircleIcon />} title={t('errors:default')} />
		)
	}
	return photosetSpaces.map((photosetSpace, index) => (
		<SpaceCard
			id={photosetSpace.id}
			key={photosetSpace.id}
			openSpaceDetailModal={() => {
				unselectAll()
				setCurrentSpaceIndex(index)
			}}
			photosetSpaceId={photosetSpace.id}
			shortcut={spaceNameToShortcut[photosetSpace.space.key_name]}
			spaceImages={[...images.values()].filter(
				(image) =>
					image.space?.key_name === photosetSpace.space.key_name
			)}
			title={spaceNameToLabel[photosetSpace.space.key_name]}
		/>
	))
}

interface SpacesSidebarProps {
	queryStatuses: UseQueryResult['status'][]
	currentSpaceIndex: number | null
	setCurrentSpaceIndex: Dispatch<SetStateAction<number | null>>
}

export const SpacesSidebar: FC<SpacesSidebarProps> = ({
	queryStatuses = [],
	currentSpaceIndex,
	setCurrentSpaceIndex
}) => {
	const { t } = useTranslation()
	const photosetSpacesDict = usePhotosetStore(
		(state) => state.photosetSpacesDict
	)
	const images = usePhotosetStore((state) => state.images)
	const unselectAll = usePhotosetStore((state) => state.unselectAll)
	const photosetSpaces = useMemo(
		() => objectValues(photosetSpacesDict),
		[photosetSpacesDict]
	)

	return (
		<div className='grid w-[336px] grid-cols-[minmax(0,1fr)] grid-rows-[auto_auto_minmax(0,1fr)]'>
			<ListHeader
				size='sm'
				title={t('photoset:allocation.sidebar_title')}
			/>
			<ScrollArea
				scrollbar={<Scrollbar />}
				style={{ overflow: 'hidden' }}
			>
				<div className='flex h-full flex-col items-stretch gap-8 overflow-y-auto py-6 pr-2 pl-6'>
					<SpacesSidebarContent
						images={images}
						photosetSpaces={photosetSpaces}
						queryStatuses={queryStatuses}
						setCurrentSpaceIndex={setCurrentSpaceIndex}
						unselectAll={unselectAll}
					/>
				</div>
			</ScrollArea>
			{currentSpaceIndex !== null && (
				<SpaceDetailModal
					currentSpaceIndex={currentSpaceIndex}
					onClose={() => {
						setCurrentSpaceIndex(null)
						unselectAll()
					}}
					photosetSpaces={photosetSpaces}
					setCurrentSpaceIndex={setCurrentSpaceIndex}
				/>
			)}
		</div>
	)
}

SpacesSidebar.displayName = 'SpacesSidebar'
